import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, LogIn, Maximize2 } from 'lucide-react';
import { useJyotishaLanguage } from '../jyotisha/JyotishaLanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { ASK_GURU_DAILY_LIMITS, getAskGuruDailyRemaining, useAskGuruQuota } from '../../hooks/useAskGuruQuota';
import { searchAskGuruContent } from '../../lib/askBhriguSearch';
import { askBhriguAi, searchBhriguContent, logBhriguCacheHit, fetchSyllabusChartSession } from '../../lib/askBhriguApi';
import { fetchPageChatMessages } from '../../lib/supabase/askBhriguSync';
import { readLocalChartLabDraft } from '../../lib/syllabusChartLab';
import { topicIdFromPageKey, numerologyTopicIdFromPageKey } from '../../utils/pageFeedbackKeys';
import {
  getBhriguStarterPrompts,
  readBhriguReferrer,
} from '../../lib/askBhriguQuerySuggestions';
import {
  BhriguSchoolScopeChips,
  useBhriguSchoolScope,
} from './BhriguSchoolScopeChips';
import {
  getAskGuruValidationMessage,
  validateAskGuruQuestion,
} from '../../lib/validateAskGuruQuestion';
import { BhriguSuggestedPrompts } from './BhriguSuggestedPrompts';
import { BhriguChatComposer } from './BhriguChatComposer';
import { BhriguChatMessage } from './BhriguChatMessage';
import { BhriguRelatedLessonLinks } from './BhriguRelatedLessonLinks';
import { BhriguCrisisCard } from './BhriguCrisisCard';
import { BhriguRecentQuestions } from './BhriguRecentQuestions';
import { BhriguQuestionPolicyModal } from './BhriguQuestionPolicyModal';
import { logActivity } from 'shared/lib/activityLog';
import { getAskGuideForSchool } from 'features/ai/lib/askGuideIdentity';

const POLICY_SEEN_KEY = 'parashari_bhrigu_policy_seen';


/**
 * @param {{
 *   isDark: boolean;
 *   onLoginClick?: () => void;
 *   onPageChange?: (pageId: string) => void;
 *   compact?: boolean;
 *   embedded?: boolean;
 *   pageKey?: string | null;
 *   pageTitle?: string | null;
 *   contextStack?: import('shared/lib/bhriguContextStack').BhriguContextStack | null;
 *   onConversationActiveChange?: (active: boolean) => void;
 *   onActiveSchoolChange?: (school: string) => void;
 *   pageShell?: boolean;
 *   panelDocked?: boolean;
 *   onDockPanel?: () => void;
 *   onRestorePanel?: () => void;
 *   onOpenChartLab?: () => void;
 * }} props
 */
export function AskGuruChat({
  isDark,
  onLoginClick,
  onPageChange,
  compact = false,
  embedded = false,
  pageKey = null,
  pageTitle = null,
  contextStack = null,
  onConversationActiveChange,
  onActiveSchoolChange,
  pageShell = false,
  panelDocked = false,
  onDockPanel,
  onRestorePanel,
  onOpenChartLab,
}) {
  const { showEnglish } = useJyotishaLanguage();
  const { user } = useAuth();
  const { quota, refresh: refreshQuota } = useAskGuruQuota();
  const messagesEndRef = useRef(null);

  const [question, setQuestion] = useState('');
  const [loadingAsk, setLoadingAsk] = useState(false);
  const [messages, setMessages] = useState([]);
  const [crisis, setCrisis] = useState(false);
  const [error, setError] = useState('');
  const [policyWarning, setPolicyWarning] = useState('');
  const [policyOpen, setPolicyOpen] = useState(false);
  const [recentRefreshKey, setRecentRefreshKey] = useState(0);
  const [hasSavedChart, setHasSavedChart] = useState(false);

  const surface = pageKey ? 'page_chat' : 'global';
  const pageTopicId = pageKey ? topicIdFromPageKey(pageKey) ?? numerologyTopicIdFromPageKey(pageKey) : null;
  const referrer = useMemo(() => readBhriguReferrer(), [pageTopicId]);
  const referrerTopicId = useMemo(
    () => (pageTopicId ? pageTopicId : referrer.topicId),
    [pageTopicId, referrer.topicId],
  );
  const showSchoolChips = !pageKey;
  const [schoolScope, setSchoolScope] = useBhriguSchoolScope({
    enabled: showSchoolChips,
    referrerSchool: showSchoolChips ? referrer.school : null,
  });
  const activeSchool = pageKey
    ? contextStack?.schoolSlug || 'parashari'
    : schoolScope;
  const guide = useMemo(() => getAskGuideForSchool(activeSchool), [activeSchool]);
  const showScopedBanner = guide.scopedSchoolOnly;
  const boostReferrerTopicIds = useMemo(() => {
    const ids = [];
    if (pageTopicId) ids.push(pageTopicId);
    else if (referrerTopicId) ids.push(referrerTopicId);
    return ids;
  }, [pageTopicId, referrerTopicId]);

  const starterPrompts = useMemo(
    () => getBhriguStarterPrompts({ topicId: referrerTopicId, limit: 5 }),
    [referrerTopicId],
  );

  const isConversationActive = messages.length > 0 || loadingAsk;

  useEffect(() => {
    onConversationActiveChange?.(isConversationActive);
  }, [isConversationActive, onConversationActiveChange]);

  useEffect(() => {
    onActiveSchoolChange?.(activeSchool);
  }, [activeSchool, onActiveSchoolChange]);

  useEffect(() => {
    if (pageKey || !onOpenChartLab) {
      setHasSavedChart(false);
      return undefined;
    }
    let cancelled = false;
    async function checkSavedChart() {
      try {
        if (user) {
          const session = await fetchSyllabusChartSession().catch(() => null);
          if (!cancelled && session?.chart) {
            setHasSavedChart(true);
            return;
          }
        }
        const local = readLocalChartLabDraft();
        if (!cancelled) {
          setHasSavedChart(Boolean(local?.chart || local?.interactive || local?.readout));
        }
      } catch {
        if (!cancelled) setHasSavedChart(false);
      }
    }
    checkSavedChart();
    return () => {
      cancelled = true;
    };
  }, [user?.id, pageKey, onOpenChartLab]);

  const dockPanel = useCallback(() => {
    if (pageShell) onDockPanel?.();
  }, [pageShell, onDockPanel]);

  useEffect(() => {
    if (pageShell && isConversationActive) {
      onDockPanel?.();
    }
  }, [pageShell, isConversationActive, onDockPanel]);

  const lastRelatedLessons = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (msg.role === 'assistant' && msg.relatedLessons?.length) {
        return msg.relatedLessons;
      }
    }
    return [];
  }, [messages]);

  const muted = isDark ? 'text-gray-400' : 'text-gray-600';
  const card = pageShell
    ? isDark
      ? 'rounded-2xl border border-white/10 bg-surface-secondary'
      : 'rounded-2xl border border-border bg-surface shadow-sm'
    : isDark
      ? 'rounded-2xl border border-white/15 bg-black/40 backdrop-blur-md'
      : 'rounded-2xl border border-amber-200/70 bg-white/88 backdrop-blur-md shadow-lg';
  const cardPad = embedded ? 'p-0' : compact ? 'p-3 sm:p-4' : 'p-4 sm:p-5';
  const shellBorder = isDark ? 'border-white/10' : 'border-amber-200/60';

  const pageShellQuotaLabel = useMemo(() => {
    if (!pageShell || !user || !quota) return null;
    if (quota.isAdmin) {
      return showEnglish ? 'Unlimited' : 'असीमित';
    }
    if ((quota.freeRemaining ?? 0) > 0) {
      return showEnglish
        ? `${quota.freeRemaining} free left`
        : `${quota.freeRemaining} मुफ़्त बचे`;
    }
    const dailyLimit = ASK_GURU_DAILY_LIMITS[surface] ?? ASK_GURU_DAILY_LIMITS.global;
    const dailyRemaining = getAskGuruDailyRemaining(quota, surface);
    return showEnglish
      ? `${dailyRemaining} of ${dailyLimit} left today`
      : `आज ${dailyRemaining}/${dailyLimit} बचे`;
  }, [pageShell, user, quota, showEnglish, surface]);

  const chartLabEntry = !pageKey && !messages.length && onOpenChartLab ? (
    <button
      type="button"
      onClick={onOpenChartLab}
      disabled={loadingAsk}
      className={`bhrigu-chart-lab-entry group w-full rounded-2xl border p-3 text-left transition-[border-color,background-color,transform,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-px disabled:opacity-50 sm:p-4 ${
        pageShell
          ? isDark
            ? 'ask-bhrigu-transparent-card border-cyan-400/30 bg-cyan-500/10 hover:border-cyan-300/45 hover:bg-cyan-500/15'
            : 'border-cyan-300/80 bg-cyan-50/90 hover:border-cyan-400 hover:bg-cyan-50'
          : isDark
            ? 'border-cyan-400/35 bg-cyan-500/10 hover:bg-cyan-500/20'
            : 'border-cyan-300 bg-cyan-50 hover:bg-cyan-100'
      }`}
    >
      <p
        className={`text-sm font-bold ${isDark ? 'text-cyan-50' : 'text-cyan-950'} ${showEnglish ? '' : 'font-devanagari'}`}
        lang={showEnglish ? 'en' : 'hi'}
      >
        {showEnglish ? 'Syllabus Chart Lab' : 'Syllabus Chart Lab'}
      </p>
      <p className={`mt-1 text-xs leading-relaxed ${muted} ${showEnglish ? '' : 'font-devanagari'}`} lang={showEnglish ? 'en' : 'hi'}>
        {showEnglish
          ? 'Place grahas on your chart - then ask which lesson concepts you studied apply here.'
          : 'चार्ट पर ग्रह रखें - फिर पूछें आपने जो पाठ सीखे, वे यहाँ कैसे लागू होते हैं।'}
      </p>
      {hasSavedChart ? (
        <p className={`mt-1.5 text-[11px] font-medium ${isDark ? 'text-amber-200/90' : 'text-amber-800'}`}>
          {showEnglish
            ? 'You have a saved chart - open Previous charts in the lab to continue.'
            : 'सहेजी कुंडली है - लैब में Previous charts से जारी रखें।'}
        </p>
      ) : null}
      <p className={`mt-2 text-[10px] font-semibold ${isDark ? 'text-cyan-200/90' : 'text-cyan-800'}`}>
        {showEnglish ? 'Open chart workspace →' : 'चार्ट वर्कस्पेस खोलें →'}
      </p>
    </button>
  ) : null;

  const pageShellHeader = pageShell ? (
    <header className={`${panelDocked ? 'mb-2 border-0 pb-0' : 'mb-4 border-b pb-4'} ${shellBorder}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className={panelDocked ? 'min-w-0 flex-1' : ''}>
          <h1
            className={`font-bold text-content ${panelDocked ? 'truncate text-base' : 'text-xl sm:text-2xl'} ${showEnglish ? '' : 'font-devanagari'}`}
            lang={showEnglish ? 'en' : 'hi'}
          >
            {guide.shortName}
          </h1>
          {!panelDocked ? (
            <p
              className={`mt-0.5 text-sm ${muted} ${showEnglish ? '' : 'font-devanagari'}`}
              lang={showEnglish ? 'en' : 'hi'}
            >
              {user
                ? showEnglish
                  ? guide.emptyTaglineEn
                  : guide.emptyTaglineHi
                : showEnglish
                  ? 'Browse recently asked questions for free. Sign in to ask something new.'
                  : 'हाल के प्रश्न मुफ़्त देखें। नया प्रश्न पूछने के लिए साइन इन करें।'}
            </p>
          ) : (
            <p
              className={`mt-0.5 text-[11px] ${muted} ${showEnglish ? '' : 'font-devanagari'}`}
              lang={showEnglish ? 'en' : 'hi'}
            >
              {showEnglish ? guide.dockedHintEn : guide.dockedHintHi}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {pageShellQuotaLabel ? (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold sm:px-3 sm:py-1 sm:text-xs ${
                isDark
                  ? 'border-amber-400/30 bg-amber-500/15 text-amber-200'
                  : 'border-amber-300/60 bg-amber-50 text-amber-900'
              }`}
            >
              {pageShellQuotaLabel}
            </span>
          ) : !user && onLoginClick && !panelDocked ? (
            <button
              type="button"
              onClick={() =>
                onLoginClick(undefined, {
                  subtitleEn: `Sign in to use ${guide.shortName}`,
                  subtitleHi: `${guide.shortName} के लिए साइन इन करें`,
                })
              }
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-content hover:bg-surface-tertiary"
            >
              <LogIn className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {showEnglish ? 'Sign in' : 'साइन इन'}
            </button>
          ) : null}
          {panelDocked && onRestorePanel ? (
            <button
              type="button"
              onClick={onRestorePanel}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
                isDark
                  ? 'border-white/15 bg-white/5 text-violet-100 hover:bg-white/10'
                  : 'border-border bg-surface text-violet-800 hover:bg-surface-tertiary'
              }`}
              aria-label={showEnglish ? 'Move chat below portrait' : 'चैट को नीचे ले जाएँ'}
            >
              <Maximize2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}
        </div>
      </div>
    </header>
  ) : null;

  const openPolicy = useCallback(() => setPolicyOpen(true), []);

  useEffect(() => {
    if (!pageKey || !user) return;
    fetchPageChatMessages(pageKey)
      .then((rows) => {
        setMessages(
          rows.map((r) => ({
            role: r.role,
            content: r.content,
          })),
        );
      })
      .catch(() => {});
  }, [pageKey, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, loadingAsk]);

  const runAiForQuestion = useCallback(
    async (q, searchLessons, searchSimilar) => {
      const topicIds = searchLessons.map((l) => l.topicId);
      if (pageTopicId && !topicIds.includes(pageTopicId)) {
        topicIds.unshift(pageTopicId);
      }

      const aiLessons = searchLessons.map((l) => ({
        id: l.topicId,
        label: l.label,
        groupLabel: l.groupLabel,
        snippet: l.snippet || '',
      }));
      if (pageTopicId && pageTitle && !aiLessons.some((l) => l.id === pageTopicId)) {
        aiLessons.unshift({ id: pageTopicId, label: pageTitle, groupLabel: '' });
      }

      const relatedForUi = searchLessons.slice(0, 4);

      const cachedQaLocal = [
        ...searchSimilar
          .filter((item) => item.answer_text)
          .slice(0, 2)
          .map((item) => ({ question: item.question_text, answer: item.answer_text })),
      ];

      const result = await askBhriguAi(
        {
          question: q,
          confirmAi: true,
          showEnglish,
          relatedTopicIds: topicIds,
          relatedLessons: aiLessons,
          cachedQa: cachedQaLocal,
          pageKey: pageKey ?? undefined,
          pageTitle: pageTitle ?? undefined,
          boostTopicIds: boostReferrerTopicIds,
          school: activeSchool,
          contextStack: contextStack ?? undefined,
        },
        surface,
      );

      if (result.crisis) {
        setCrisis(true);
        setMessages((m) => [
          ...m,
          { role: 'assistant', content: result.reply, crisis: true, relatedLessons: relatedForUi },
        ]);
      } else if (result.blocked) {
        setMessages((m) => [...m, { role: 'assistant', content: result.reply, relatedLessons: relatedForUi }]);
        if (result.policyWarning) {
          setPolicyWarning(
            showEnglish
              ? result.policyWarning
              : 'यह प्रश्न सहेजा गया। बार-बार नीति उल्लंघन पर खाता निलंबित हो सकता है।',
          );
        }
        if (result.cooldown || result.suspended) {
          setError(result.error || (showEnglish ? 'Cooldown active' : 'प्रतीक्षा आवश्यक'));
        }
        if (!localStorage.getItem(POLICY_SEEN_KEY)) {
          setPolicyOpen(true);
          localStorage.setItem(POLICY_SEEN_KEY, '1');
        }
      } else if (result.retrievalWeak) {
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content: result.reply,
            relatedLessons: relatedForUi.length ? relatedForUi : (result.relatedLessons || []).slice(0, 4),
          },
        ]);
      } else if (result.reply) {
        setMessages((m) => [...m, { role: 'assistant', content: result.reply, relatedLessons: relatedForUi }]);
        if (contextStack) {
          logActivity({
            action: 'bhrigu.question_asked',
            summary: `Asked Bhrigu (${contextStack.layer} context)`,
            app: 'ai',
            institutionId: contextStack.institutionId,
            resourceType: contextStack.batchId ? 'batch' : contextStack.lessonId ? 'lesson' : 'school',
            resourceId: contextStack.batchId ?? contextStack.lessonId ?? contextStack.schoolSlug,
            metadata: { ...contextStack, pageKey },
          });
        }
      }

      await refreshQuota();
      setRecentRefreshKey((k) => k + 1);
    },
    [showEnglish, pageTopicId, pageTitle, pageKey, surface, refreshQuota, boostReferrerTopicIds, activeSchool, contextStack],
  );

  const handleAsk = useCallback(
    async (overrideQuery, askOpts = {}) => {
      const q = String(overrideQuery ?? question).trim();
      if (loadingAsk) return;

      if (!askOpts.skipValidation) {
        const validation = validateAskGuruQuestion(q);
        if (!validation.valid) {
          setError(getAskGuruValidationMessage(validation, showEnglish));
          return;
        }
      } else if (q.length < 2) {
        return;
      }

      setError('');
      setPolicyWarning('');
      setCrisis(false);
      setLoadingAsk(true);
      setQuestion('');

      setMessages((m) => [...m, { role: 'user', content: q }]);

      const boostTopicIds = [...boostReferrerTopicIds];
      if (pageTopicId && !boostTopicIds.includes(pageTopicId)) {
        boostTopicIds.unshift(pageTopicId);
      }
      let searchLessons = [];
      let searchSimilar = [];
      let searchCacheHit = false;

      try {
        const result = await searchBhriguContent(q, {
          limit: embedded ? 3 : 5,
          lessonLimit: 6,
          showEnglish,
          pageKey: pageKey ?? undefined,
          pageTitle: pageTitle ?? undefined,
          boostTopicIds,
          school: activeSchool,
        });

        searchLessons = (result.lessons || []).map((l) => ({
          topicId: l.topicId,
          label: l.label,
          groupLabel: l.groupLabel,
          snippet: l.snippet || '',
          section: l.section || 'jyotisha',
        }));

        if (searchLessons.length === 0) {
          searchLessons = searchAskGuruContent(q, pageTopicId ? { limit: 6 } : undefined);
        }

        searchSimilar = result.similar || [];
        searchCacheHit = Boolean(result.cacheHit);
      } catch (err) {
        if (err?.data?.code === 'invalid_question') {
          setError(
            getAskGuruValidationMessage(
              {
                messageEn: err.data.messageEn || err.message,
                messageHi: err.data.messageHi || err.message,
              },
              showEnglish,
            ),
          );
          setLoadingAsk(false);
          return;
        }
        searchLessons = searchAskGuruContent(q, pageTopicId ? { limit: 6 } : undefined);
        searchSimilar = [];
        searchCacheHit = false;
        const msg = err instanceof Error ? err.message : '';
        if (msg.includes('server') || msg.includes('fetch') || msg.includes('Network')) {
          setError(
            showEnglish
              ? 'Could not reach AskGuru API. Restart with: npm run dev (port 3001 must be free).'
              : 'AskGuru API तक नहीं पहुँचे। npm run dev दोबारा चलाएँ।',
          );
        }
      }

      const topSimilar = searchSimilar[0];
      const hasCachedAnswer = Boolean(
        (searchCacheHit || (topSimilar?.sim >= 0.85 && topSimilar?.answer_text)) && topSimilar?.answer_text,
      );

      if (hasCachedAnswer) {
        const relatedForUi = searchLessons.slice(0, 4);
        setMessages((m) => [
          ...m,
          { role: 'assistant', content: topSimilar.answer_text, cacheHit: true, relatedLessons: relatedForUi },
        ]);
        if (user) {
          try {
            await logBhriguCacheHit({
              question: q,
              similarQuestionId: topSimilar.id,
              pageKey: pageKey ?? undefined,
              pageTitle: pageTitle ?? undefined,
              relatedTopicIds: searchLessons.map((l) => l.topicId),
            });
            setRecentRefreshKey((k) => k + 1);
          } catch {
            /* non-blocking */
          }
        }
        setLoadingAsk(false);
        return;
      }

      if (!user) {
        setError(
          showEnglish
            ? `Sign in to get a ${guide.displayNameEn} answer.`
            : `${guide.displayNameHi} से उत्तर पाने के लिए साइन इन करें।`,
        );
        if (onLoginClick) {
          onLoginClick(undefined, {
            subtitleEn: `Sign in to use ${guide.shortName}`,
            subtitleHi: `${guide.shortName} के लिए साइन इन करें`,
          });
        }
        setLoadingAsk(false);
        return;
      }

      try {
        await runAiForQuestion(q, searchLessons, searchSimilar);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Request failed';
        if (err?.data?.code === 'invalid_question') {
          setError(
            getAskGuruValidationMessage(
              {
                messageEn: err.data.messageEn || msg,
                messageHi: err.data.messageHi || msg,
              },
              showEnglish,
            ),
          );
        } else {
          setError(msg);
        }
        if (err?.status === 402) {
          setError(
            showEnglish
              ? "You've reached today's AskGuru limit. Try again tomorrow."
              : 'आज की AskGuru सीमा पूरी हो गई। कल फिर कोशिश करें।',
          );
        }
        if (err?.data?.policyWarning) {
          setPolicyWarning(
            showEnglish
              ? err.data.policyWarning
              : 'यह प्रश्न सहेजा गया। बार-बार नीति उल्लंघन पर खाता निलंबित हो सकता है।',
          );
        }
      } finally {
        setLoadingAsk(false);
      }
    },
    [question, loadingAsk, showEnglish, embedded, pageTopicId, pageKey, pageTitle, user, runAiForQuestion, onLoginClick, boostReferrerTopicIds, activeSchool, guide],
  );

  const startNewConversation = useCallback(() => {
    setMessages([]);
    setQuestion('');
    setError('');
    setPolicyWarning('');
    setCrisis(false);
    onConversationActiveChange?.(false);
  }, [onConversationActiveChange]);

  const handleRecentSelect = useCallback(
    async (item) => {
      const q = String(item.question_text || '').trim();
      if (q.length < 2) return;

      if (item.answer_text) {
        setError('');
        setPolicyWarning('');
        setCrisis(false);
        setMessages((m) => [
          ...m,
          { role: 'user', content: q },
          {
            role: 'assistant',
            content: item.answer_text,
            cacheHit: true,
          },
        ]);
        if (user && item.id) {
          try {
            await logBhriguCacheHit({
              question: q,
              similarQuestionId: item.id,
              pageKey: pageKey ?? undefined,
              pageTitle: pageTitle ?? undefined,
              relatedTopicIds: pageTopicId ? [pageTopicId] : [],
            });
            setRecentRefreshKey((k) => k + 1);
          } catch {
            /* non-blocking */
          }
        }
        return;
      }

      handleAsk(q, { skipValidation: true });
    },
    [user, pageKey, pageTitle, pageTopicId, handleAsk],
  );

  const schoolScopeBanner = showScopedBanner ? (
    <div
      className={`rounded-lg border px-3 py-2 text-[11px] leading-relaxed ${
        isDark
          ? 'border-amber-400/35 bg-amber-950/25 text-amber-100'
          : 'border-amber-300 bg-amber-50 text-amber-950'
      } ${showEnglish ? '' : 'font-devanagari'}`}
      lang={showEnglish ? 'en' : 'hi'}
      role="status"
    >
      {showEnglish ? guide.scopeHintEn : guide.scopeHintHi}
    </div>
  ) : null;

  const errorBanner = error ? (
    <div
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
        isDark ? 'border-rose-400/40 bg-rose-950/30 text-rose-200' : 'border-rose-200 bg-rose-50 text-rose-800'
      }`}
      role="alert"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
      {error}
    </div>
  ) : null;

  const policyBanner = policyWarning ? (
    <div
      className={`rounded-lg border px-3 py-2 text-xs leading-relaxed ${
        isDark ? 'border-amber-400/30 bg-amber-950/20 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-950'
      }`}
      role="status"
    >
      {policyWarning}{' '}
      <button type="button" onClick={openPolicy} className="font-semibold underline underline-offset-2">
        {showEnglish ? 'Question policy' : 'प्रश्न नीति'}
      </button>
    </div>
  ) : null;

  const composerProps = {
    value: question,
    onChange: setQuestion,
    onSubmit: () => handleAsk(),
    onPickSuggestion: (text) => handleAsk(text, { skipValidation: true }),
    onFocus: pageShell ? dockPanel : undefined,
    showEnglish,
    isDark,
    loading: loadingAsk,
    compact: panelDocked ? false : compact || embedded,
    embedded,
    canSubmit: Boolean(question.trim()),
    placeholder:
      pageTitle && embedded
        ? showEnglish
          ? showScopedBanner
            ? `Ask ${guide.displayNameEn} about "${pageTitle.slice(0, 36)}"…`
            : `Ask about "${pageTitle.slice(0, 40)}"…`
          : showScopedBanner
            ? guide.placeholderHi
            : 'इस पाठ से जुड़ा प्रश्न…'
        : showEnglish
          ? guide.placeholderEn
          : guide.placeholderHi,
  };

  const embeddedStarterPrompts = useMemo(
    () => (embedded ? starterPrompts.slice(0, 2) : []),
    [embedded, starterPrompts],
  );

  const schoolScopeChips = showSchoolChips ? (
    <BhriguSchoolScopeChips
      showEnglish={showEnglish}
      isDark={isDark}
      value={schoolScope}
      onChange={setSchoolScope}
      disabled={loadingAsk}
      compact={embedded}
    />
  ) : null;

  const embeddedLanding = (
    <div className={`bhrigu-landing-enter flex min-h-0 flex-1 flex-col gap-0 ${cardPad}`}>
      {schoolScopeChips}
      {schoolScopeBanner ? <div className="mb-2 shrink-0">{schoolScopeBanner}</div> : null}
      <div className="relative z-10 shrink-0">
        <BhriguChatComposer {...composerProps} variant="landing" />
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain">
        {embeddedStarterPrompts.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 pt-2">
            {embeddedStarterPrompts.map((p) => {
              const text = showEnglish ? p.en : p.hi;
              return (
                <button
                  key={p.en}
                  type="button"
                  disabled={loadingAsk}
                  onClick={() => handleAsk(text, { skipValidation: true })}
                  className={`rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors disabled:opacity-50 ${
                    isDark
                      ? 'border-violet-400/30 bg-violet-500/10 text-violet-100 hover:bg-violet-500/20'
                      : 'border-violet-300/70 bg-violet-50 text-violet-900 hover:bg-violet-100'
                  } ${showEnglish ? '' : 'font-devanagari'}`}
                  lang={showEnglish ? 'en' : 'hi'}
                >
                  {text}
                </button>
              );
            })}
          </div>
        ) : null}

        <BhriguRecentQuestions
          isDark={isDark}
          showEnglish={showEnglish}
          refreshKey={recentRefreshKey}
          collapsible
          compact
          maxItems={4}
          defaultOpen={false}
          onSelectQuestion={handleRecentSelect}
        />
      </div>

    </div>
  );

  const mainLanding = panelDocked ? (
    <div className={`bhrigu-landing-enter flex h-full min-h-0 flex-col ${card} p-3 sm:p-4`}>
      {pageShellHeader}
      <div className={`shrink-0 border-b pb-3 ${shellBorder}`}>
        {chartLabEntry ? <div className="mb-3">{chartLabEntry}</div> : null}
        {schoolScopeChips}
        {schoolScopeBanner ? <div className="mb-2">{schoolScopeBanner}</div> : null}
        <BhriguChatComposer {...composerProps} variant="landing" />
      </div>
      <div className="mt-3 min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pr-0.5">
        <BhriguSuggestedPrompts
          showEnglish={showEnglish}
          isDark={isDark}
          prompts={starterPrompts}
          disabled={loadingAsk}
          onSelect={(text) => handleAsk(text, { skipValidation: true })}
        />
        <BhriguRecentQuestions
          isDark={isDark}
          showEnglish={showEnglish}
          refreshKey={recentRefreshKey}
          collapsible={false}
          compact
          defaultOpen
          isSignedIn={Boolean(user)}
          onSelectQuestion={handleRecentSelect}
        />
      </div>
    </div>
  ) : (
    <div className={`bhrigu-landing-enter ${cardPad} ${card}`}>
      {pageShellHeader}
      {chartLabEntry ? <div className="mb-3">{chartLabEntry}</div> : null}
      {schoolScopeChips}
      {schoolScopeBanner ? <div className="mb-3">{schoolScopeBanner}</div> : null}

      <div className="mb-4">
        <BhriguChatComposer {...composerProps} variant="landing" />
      </div>

      <BhriguSuggestedPrompts
        showEnglish={showEnglish}
        isDark={isDark}
        prompts={starterPrompts}
        disabled={loadingAsk}
        onSelect={(text) => handleAsk(text, { skipValidation: true })}
      />

      <div className="mt-4">
        <BhriguRecentQuestions
          isDark={isDark}
          showEnglish={showEnglish}
          refreshKey={recentRefreshKey}
          collapsible={false}
          defaultOpen
          isSignedIn={Boolean(user)}
          onSelectQuestion={handleRecentSelect}
        />
      </div>
    </div>
  );

  return (
    <div
      className={`flex flex-col ${embedded ? 'min-h-0 flex-1' : compact ? '' : 'w-full'} ${
        pageShell && panelDocked
          ? 'h-full min-h-0'
          : isConversationActive
            ? embedded
              ? 'min-h-0 flex-1'
              : 'min-h-[min(60dvh,32rem)]'
            : embedded
              ? 'min-h-0 flex-1'
              : 'space-y-4'
      }`}
    >
      <BhriguQuestionPolicyModal
        open={policyOpen}
        onClose={() => setPolicyOpen(false)}
        isDark={isDark}
        showEnglish={showEnglish}
      />

      {!isConversationActive ? (
        embedded ? embeddedLanding : mainLanding
      ) : (
        <>
          <div
            className={`bhrigu-conversation-enter flex min-h-0 flex-1 flex-col ${embedded ? 'h-full' : `${card} ${panelDocked ? 'h-full p-3 sm:p-4' : cardPad}`}`}
            aria-live="polite"
          >
            {pageShellHeader}
            <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-0.5 pb-2">
              {messages.map((msg, i) => (
                <BhriguChatMessage
                  key={`${msg.role}-${i}`}
                  role={msg.role}
                  content={msg.content}
                  isDark={isDark}
                  showEnglish={showEnglish}
                  crisis={msg.crisis}
                  cacheHit={msg.cacheHit}
                  animate={i === messages.length - 1}
                  school={activeSchool}
                />
              ))}
              {loadingAsk ? (
                <BhriguChatMessage
                  role="assistant"
                  isDark={isDark}
                  showEnglish={showEnglish}
                  loading
                  animate
                  school={activeSchool}
                />
              ) : null}
              {errorBanner}
              {policyBanner}
              {crisis ? <BhriguCrisisCard isDark={isDark} showEnglish={showEnglish} /> : null}
              <div ref={messagesEndRef} />
            </div>

            <div
              className={`shrink-0 border-t pt-2 ${shellBorder}`}
            >
              {!embedded && panelDocked ? (
                <div className="mb-2 flex justify-end">
                  <button
                    type="button"
                    onClick={startNewConversation}
                    className={`text-[10px] font-semibold underline-offset-2 hover:underline ${
                      isDark ? 'text-violet-300' : 'text-violet-700'
                    }`}
                  >
                    {showEnglish ? 'New conversation' : 'नई बातचीत'}
                  </button>
                </div>
              ) : null}
              {!panelDocked ? (
                <BhriguRelatedLessonLinks
                  lessons={lastRelatedLessons}
                  isDark={isDark}
                  showEnglish={showEnglish}
                  onPageChange={onPageChange}
                />
              ) : null}
              {!embedded && !panelDocked ? (
                <div className="mb-2 flex justify-end">
                  <button
                    type="button"
                    onClick={startNewConversation}
                    className={`text-[10px] font-semibold underline-offset-2 hover:underline ${
                      isDark ? 'text-violet-300' : 'text-violet-700'
                    }`}
                  >
                    {showEnglish ? 'New conversation' : 'नई बातचीत'}
                  </button>
                </div>
              ) : null}
              {panelDocked ? (
                <BhriguRelatedLessonLinks
                  lessons={lastRelatedLessons}
                  isDark={isDark}
                  showEnglish={showEnglish}
                  onPageChange={onPageChange}
                />
              ) : null}
              <BhriguChatComposer {...composerProps} variant="chat" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AskGuruChat;
